import { IconRefresh } from './icons.js';
import { useClaimOfflineBanner } from '../../pwa/offlineBannerClaim.js';
import { useOnlineStatus } from '../../pwa/useOnlineStatus.js';
import type { UseConversationController } from './useConversationController.js';

interface ConnectionBannerProps {
  status: UseConversationController['connectionStatus'];
  onReconnect: () => void;
  onLeave: () => void;
}

/**
 * Banner del estado de la conexión WS (Fase 07). Reemplaza el viejo síntoma de
 * "ondas congeladas" cuando el backend se caía: ahora la caída es visible y
 * accionable.
 *
 *  - `reconnecting` → barra discreta con spinner; la auto-reconexión corre sola
 *    (reusa `resumeSessionId`, el coach retoma el contexto).
 *  - `lost` → se agotó la ráfaga con backoff: card con botón "Reconectar" y
 *    "Volver". El hook igual sigue reintentando solo desde este estado (al
 *    volver la red / el tab, y cada tanto), así que la card puede desaparecer
 *    sola — el botón es el atajo, no la única salida.
 *
 * Es el ÚNICO cartel de conexión mientras está visible: reclama el lugar del
 * pill app-wide "Sin conexión" de `PwaLayer` (los dos decían lo mismo, uno
 * arriba del otro) y absorbe su info — sin red según `navigator.onLine` el
 * título pasa a "Sin conexión"; con red, la caída es del lado del coach.
 *
 * En `idle` / `connecting` / `open` no renderiza nada (y el pill vuelve a
 * cubrir). El cierre server-side TERMINAL (`session.ended`) no pasa por acá:
 * lo muestra `SessionEndedCard` al final del hilo de mensajes (tras el cierre
 * el WS vuelve a 'idle', así que este banner queda mudo — no hay solape).
 */
export function ConnectionBanner({ status, onReconnect, onLeave }: ConnectionBannerProps) {
  const { online } = useOnlineStatus();
  useClaimOfflineBanner(status === 'reconnecting' || status === 'lost');

  if (status === 'reconnecting') {
    return (
      <div className="conn-banner conn-banner-retry" role="status" aria-live="polite">
        <span className="conn-spinner" aria-hidden="true" />
        <span>{online ? 'Reconectando con el coach…' : 'Sin conexión — reconectando…'}</span>
      </div>
    );
  }

  if (status === 'lost') {
    return (
      <div className="conn-banner conn-banner-lost" role="alert">
        <div className="conn-lost-text">
          <strong>{online ? 'Se perdió la conexión con el coach' : 'Sin conexión'}</strong>
          <span>
            Reintentamos solos, o tocá Reconectar — el coach sigue donde estaba. El último turno
            pudo perderse.
          </span>
        </div>
        <div className="conn-lost-actions">
          <button type="button" className="conn-btn conn-btn-primary" onClick={onReconnect}>
            <IconRefresh size={14} /> Reconectar
          </button>
          <button type="button" className="conn-btn conn-btn-ghost" onClick={onLeave}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return null;
}
